import { GameState } from '../StateManager';
import { Game } from '../Game';
import { Player } from '../entities/Player';
import { PowerUp } from '../entities/PowerUp';
import { CollisionSystem } from '../systems/CollisionSystem';
import { ProjectileSystem } from '../systems/ProjectileSystem';
import { WaveManager } from '../systems/WaveManager';
import { ChainSystem } from '../systems/ChainSystem';
import { ScoreSystem } from '../systems/ScoreSystem';
import { WeaponSystem } from '../systems/WeaponSystem';
import { ParticleSystem } from '../systems/ParticleSystem';
import { HUD } from '../ui/HUD';
import { UnityChain } from '../ui/UnityChain';
import { KidKellyMessages } from '../ui/KidKellyMessages';
import { WeaponHUD } from '../ui/WeaponHUD';
import { SoundManager } from '../audio/SoundManager';

export class PlayState implements GameState {
  private game: Game;
  private player: Player;
  private collisionSystem: CollisionSystem;
  private projectileSystem: ProjectileSystem;
  private waveManager: WaveManager;
  private chainSystem: ChainSystem;
  private scoreSystem: ScoreSystem;
  private weaponSystem: WeaponSystem;
  private particleSystem: ParticleSystem;
  private soundManager: SoundManager;
  private hud: HUD;
  private unityChain: UnityChain;
  private kidKellyMessages: KidKellyMessages;
  private weaponHUD: WeaponHUD;
  private powerUps: PowerUp[] = [];
  
  // Background image
  private backgroundImage: HTMLImageElement | null = null;
  private bgImageLoaded: boolean = false;
  
  // Backtrax logo
  private backtraxLogo: HTMLImageElement | null = null;
  private backtraxLogoLoaded: boolean = false;
  
  // State management
  private currentRegion: number = 1;
  private currentWave: number = 1;
  private waveTransitionTimer: number = 0;
  private powerUpNotificationTimer: number = 0;
  private lastPowerUpName: string = '';
  private bossWarningTimer: number = 0;
  private bossName: string = '';
  private midWaveMessageTimer: number = 0; // For random encouragement messages

  constructor(game: Game) {
    this.game = game;
    
    // Background image will be loaded dynamically based on region
    this.backgroundImage = new Image();
    this.updateBackgroundForRegion(1); // Start with region 1
    
    // Load Backtrax logo
    this.backtraxLogo = new Image();
    this.backtraxLogo.src = 'assets/backtrax_logo.png';
    this.backtraxLogo.onload = () => {
      this.backtraxLogoLoaded = true;
    };
    
    // Initialize systems
    const canvas = game.getCanvas();
    this.player = new Player(canvas.width / 2, canvas.height - 100);
    this.collisionSystem = new CollisionSystem();
    this.projectileSystem = new ProjectileSystem();
    this.waveManager = new WaveManager();
    this.chainSystem = new ChainSystem();
    this.scoreSystem = new ScoreSystem();
    this.weaponSystem = new WeaponSystem();
    this.particleSystem = new ParticleSystem();
    this.soundManager = game.getSoundManager();
    this.hud = new HUD(canvas);
    this.unityChain = new UnityChain(canvas.width, canvas.height);
    this.kidKellyMessages = new KidKellyMessages();
    this.weaponHUD = new WeaponHUD();
  }

  public enter(): void {
    console.log('Entered Play State');
    this.resetGame();
    this.startNextWave();
    this.weaponHUD.show();
    
    // Show the game HUD
    const gameHud = document.getElementById('gameHud');
    if (gameHud) {
      gameHud.classList.add('visible');
    }
  }

  public exit(): void {
    console.log('Exited Play State');
    this.weaponHUD.hide();
    
    // Hide the game HUD
    const gameHud = document.getElementById('gameHud');
    if (gameHud) {
      gameHud.classList.remove('visible');
    }
  }

  private resetGame(): void {
    // Reset all systems
    const canvas = this.game.getCanvas();
    this.player = new Player(canvas.width / 2, canvas.height - 100);
    this.projectileSystem.clear();
    this.waveManager.reset();
    this.chainSystem.reset();
    this.scoreSystem.reset();
    this.powerUps = [];
    this.currentRegion = 1;
    this.currentWave = 1;
  }

  private startNextWave(): void {
    this.waveManager.startWave(this.currentRegion, this.currentWave);
    this.kidKellyMessages.showWaveStart(this.currentWave);
    
    const waveState = this.waveManager.getWaveState();
    if (waveState.isBoss || waveState.isMiniBoss) {
      // Show boss warning
      const boss = this.waveManager.getBoss();
      if (boss) {
        this.bossName = boss.getName();
        this.bossWarningTimer = 3; // 3 seconds warning
      }
    }
  }

  public update(deltaTime: number): void {
    const input = this.game.getInput();

    // Check for pause
    if (input.escape) {
      this.game.getStateManager().changeState('pause');
      return;
    }

    // Switch weapons with number keys 1-6
    for (let i = 1; i <= 6; i++) {
      if (input.isNumberPressed(i)) {
        this.weaponSystem.switchWeapon(i);
        this.weaponHUD.updateWeapon(i);
        this.soundManager.play('powerup');
        console.log('Switched to weapon:', i);
        break;
      }
    }

    // Update timers
    if (this.waveTransitionTimer > 0) {
      this.waveTransitionTimer -= deltaTime;
      if (this.waveTransitionTimer <= 0) {
        this.advanceWave();
      }
      return; // Don't update game during transition
    }

    if (this.powerUpNotificationTimer > 0) {
      this.powerUpNotificationTimer -= deltaTime;
    }

    if (this.bossWarningTimer > 0) {
      this.bossWarningTimer -= deltaTime;
      return; // Pause game during boss warning
    }
    
    // Mid-wave encouragement messages (every 10-15 seconds)
    this.midWaveMessageTimer += deltaTime;
    if (this.midWaveMessageTimer > 10 + Math.random() * 5) {
      this.kidKellyMessages.showMidWaveEncouragement();
      this.midWaveMessageTimer = 0;
    }

    // Update player
    this.player.update(deltaTime);

    // Player movement (4-directional like original)
    const canvas = this.game.getCanvas();
    const speed = 7;
    
    let dx = 0;
    if (input.left && this.player.x > this.player.width / 2) {
      dx = -1;
    }
    if (input.right && this.player.x < canvas.width - this.player.width / 2) {
      dx = 1;
    }
    
    // Update player facing direction based on horizontal movement
    if (dx !== 0) {
      this.player.move(dx, canvas.width, canvas.height);
    }
    
    if (input.up && this.player.y > 20) {
      this.player.y -= speed;
    }
    if (input.down && this.player.y < canvas.height - this.player.height) {
      this.player.y += speed;
    }

    // Player shooting
    if (input.fire) {
      const projectiles = this.weaponSystem.fire(this.player.x, this.player.y);
      if (projectiles.length > 0) {
        // Play weapon sound based on weapon type
        const weaponType = this.weaponSystem.getCurrentWeapon();
        this.playWeaponSound(weaponType);
        
        for (const proj of projectiles) {
          this.projectileSystem.addWeaponProjectile(proj);
        }
        
        // Muzzle flash effect
        this.particleSystem.createTrail(this.player.x, this.player.y - 20, '#00ffff');
      }
    }

    // Update systems
    this.projectileSystem.update(deltaTime);
    this.waveManager.update(deltaTime);
    this.chainSystem.update(deltaTime);
    this.scoreSystem.update(deltaTime);
    this.unityChain.update(deltaTime);
    this.particleSystem.update(deltaTime);

    // Apply rapid fire modifier if active
    if (this.player.hasRapidFire()) {
      this.weaponSystem.setFireRateModifier(0.5); // 2x faster
    } else {
      this.weaponSystem.setFireRateModifier(1.0); // Normal speed
    }

    // Update weapon HUD with current weapon, powerups, and health
    this.weaponHUD.updateWeapon(this.weaponSystem.getCurrentWeapon());
    this.weaponHUD.updatePowerUps(this.player.getActivePowerUps());
    this.weaponHUD.updateHealth(this.player.getLives());

    // Update power-ups
    for (const powerUp of this.powerUps) {
      if (powerUp.active) {
        powerUp.update(deltaTime);
      }
    }

    // Create projectile trails for visual flair
    const projectiles = this.projectileSystem.getProjectiles();
    for (const proj of projectiles) {
      if (proj.active && proj.isFriendly() && Math.random() < 0.3) {
        this.particleSystem.createTrail(proj.x, proj.y, proj.getColor());
      }
    }

    // Handle collisions
    this.handleCollisions();

    // Check wave completion
    if (this.waveManager.isWaveComplete()) {
      this.onWaveComplete();
    }

    // Check game over
    if (!this.player.active) {
      const gameOverState = this.game.getStateManager().getCurrentState() as any;
      if (gameOverState && gameOverState.setScore) {
        gameOverState.setScore(this.scoreSystem.getScore());
        gameOverState.setVictory(false);
      }
      this.game.getStateManager().changeState('gameover');
    }
  }

  private handleCollisions(): void {
    // Weapon projectiles vs enemies (new system)
    const weaponHits = this.collisionSystem.checkProjectileEnemyCollisions(
      this.waveManager.getEnemies(),
      this.projectileSystem.getWeaponProjectiles()
    );

    let shotgunKillsThisFrame = 0;

    for (const hit of weaponHits) {
      // Play impact sound
      this.soundManager.play('enemyHit');
      
      // Calculate pushback based on weapon type
      const pushbackForce = hit.projectile.weaponType === 6 ? 120 : 50;
      hit.enemy.y -= pushbackForce * 0.016;
      
      // Apply enemy velocity pushback (matching original)
      const enemyVelocity = hit.enemy as any; // Type assertion for dy property
      if (enemyVelocity.dy) {
        const velocityChange = Math.random() * -8 + Math.random() * -7; // -8 to -15
        enemyVelocity.dy = Math.max(velocityChange, enemyVelocity.dy);
      }
      
      const defeated = hit.enemy.takeDamage(hit.projectile.damage);
      if (defeated) {
        this.soundManager.play('explosion');
        this.scoreSystem.addScore(hit.enemy.getScore(), this.player.getScoreMultiplier());
        this.dropPowerUp(hit.enemy.x, hit.enemy.y);
        
        // Explosion particles
        this.particleSystem.createExplosion(hit.enemy.x, hit.enemy.y, '#ff8800', 25);
        
        // Screen shake for heavy weapon impact (Harmony Blast)
        if (hit.projectile.weaponType === 6) {
          this.game.getRenderer().addScreenShake('medium');
        }
        
        // Track shotgun kills for cluster destruction shake
        if (hit.projectile.weaponType === 5) { // Unity Spread
          shotgunKillsThisFrame++;
        }
        
        // Combo effect on multi-kills
        if (this.scoreSystem.getCombo() > 2) {
          this.particleSystem.createComboEffect(hit.enemy.x, hit.enemy.y);
        }
        
        // Greed split behavior disabled - was causing exponential enemy multiplication
      }
      
      // Handle piercing
      if (hit.projectile.piercing && hit.projectile.piercing > 0) {
        hit.projectile.piercing--;
        if (hit.projectile.piercing <= 0) {
          hit.projectile.active = false;
        }
      } else {
        hit.projectile.active = false;
      }
    }
    
    // Screen shake for shotgun cluster destruction (3+ enemies)
    if (shotgunKillsThisFrame >= 3) {
      this.game.getRenderer().addScreenShake('light');
    }
    
    // Player bullets vs enemies (old system fallback)
    const enemyHits = this.collisionSystem.checkPlayerBulletEnemies(
      this.projectileSystem.getProjectiles(),
      this.waveManager.getEnemies()
    );

    for (const hit of enemyHits) {
      this.soundManager.play('enemyHit');
      const defeated = hit.enemy.takeDamage(hit.projectile.getDamage());
      if (defeated) {
        this.soundManager.play('explosion');
        this.scoreSystem.addScore(hit.enemy.getScore(), this.player.getScoreMultiplier());
        this.dropPowerUp(hit.enemy.x, hit.enemy.y);
        
        // Explosion particles
        this.particleSystem.createExplosion(hit.enemy.x, hit.enemy.y, '#ff8800', 25);
        
        // Combo effect on multi-kills
        if (this.scoreSystem.getCombo() > 2) {
          this.particleSystem.createComboEffect(hit.enemy.x, hit.enemy.y);
        }
        
        // Greed split behavior disabled - was causing exponential enemy multiplication
      }
    }

    // Player bullets vs boss
    const boss = this.waveManager.getBoss();
    const bossHits = this.collisionSystem.checkPlayerBulletBoss(
      this.projectileSystem.getProjectiles(),
      boss
    );

    for (const hit of bossHits) {
      this.soundManager.play('enemyHit');
      const defeated = hit.boss.takeDamage(hit.projectile.getDamage());
      if (defeated) {
        this.soundManager.play('explosion');
        this.scoreSystem.addScore(hit.boss.getScore(), this.player.getScoreMultiplier());
        this.scoreSystem.addBonus(1000); // Boss defeat bonus
      }
    }

    // Player vs enemies
    const enemyCollisions = this.collisionSystem.checkPlayerEnemies(
      this.player,
      this.waveManager.getEnemies()
    );

    if (enemyCollisions.length > 0) {
      // Check if player has shield active
      if (this.player.hasShield()) {
        // Shield bounces enemies away and destroys them on contact with other enemies
        for (const enemy of enemyCollisions) {
          // Calculate bounce direction (opposite of enemy movement toward player)
          const dx = enemy.x - this.player.x;
          const dy = enemy.y - this.player.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance > 0) {
            // Normalize direction and apply bounce force
            const bounceForce = 8; // Strong bounce velocity
            const normalizedDx = dx / distance;
            const normalizedDy = dy / distance;
            
            enemy.setVelocity(normalizedDx * bounceForce, normalizedDy * bounceForce);
          }
        }
      } else {
        // No shield - player takes damage
        this.soundManager.play('playerHit');
        this.player.takeDamage();
        this.scoreSystem.resetCombo();
      }
    }

    // Enemy-enemy collisions (for shield bounce chain reactions)
    const enemyEnemyCollisions = this.collisionSystem.checkEnemyEnemyCollisions(
      this.waveManager.getEnemies()
    );

    for (const collision of enemyEnemyCollisions) {
      // Check if either enemy is moving fast (bounced from shield)
      const enemy1Vel = collision.enemy1.getVelocity();
      const enemy2Vel = collision.enemy2.getVelocity();
      const enemy1Speed = Math.sqrt(enemy1Vel.dx * enemy1Vel.dx + enemy1Vel.dy * enemy1Vel.dy);
      const enemy2Speed = Math.sqrt(enemy2Vel.dx * enemy2Vel.dx + enemy2Vel.dy * enemy2Vel.dy);
      
      // If one enemy is moving fast (bounced), destroy the other
      if (enemy1Speed > 5 || enemy2Speed > 5) {
        // Destroy both enemies in the collision
        this.soundManager.play('explosion');
        this.particleSystem.createExplosion(collision.enemy1.x, collision.enemy1.y, '#ff8800', 25);
        this.particleSystem.createExplosion(collision.enemy2.x, collision.enemy2.y, '#ff8800', 25);
        
        // Award score for both enemies
        this.scoreSystem.addScore(collision.enemy1.getScore(), this.player.getScoreMultiplier());
        this.scoreSystem.addScore(collision.enemy2.getScore(), this.player.getScoreMultiplier());
        
        collision.enemy1.takeDamage(999);
        collision.enemy2.takeDamage(999);
      }
    }

    // Enemies vs chain people (damages chain segments directly)
    const chainCollisions = this.collisionSystem.checkEnemyChainCollisions(
      this.waveManager.getEnemies(),
      this.unityChain.getPeopleBounds()
    );

    if (chainCollisions.length > 0) {
      this.soundManager.play('playerHit');
      
      // Trigger visual effects
      this.unityChain.triggerHitFlash();
      this.game.getRenderer().addScreenShake('medium');
      
      // Damage specific chain segments
      for (const collision of chainCollisions) {
        const result = this.unityChain.damageSegment(collision.segmentIndex, 1);
        
        if (result.gameOver) {
          // 3 adjacent segments broken - game over!
          this.game.getStateManager().changeState('gameover', { victory: false, score: this.scoreSystem.getScore() });
          return;
        }
        
        // Heavy shake when chain enters critical state
        if (result.enteredCritical) {
          this.game.getRenderer().addScreenShake('heavy');
        }
        
        // Also damage player for each chain hit
        this.player.takeDamage();
        this.scoreSystem.resetCombo();
        
        // Destroy enemy that hit the chain
        collision.enemy.takeDamage(999);
      }
    }

    // Player vs boss
    if (this.collisionSystem.checkPlayerBoss(this.player, boss)) {
      this.soundManager.play('playerHit');
      this.player.takeDamage();
      this.scoreSystem.resetCombo();
    }

    // Player vs power-ups
    const powerUpCollisions = this.collisionSystem.checkPlayerPowerUps(
      this.player,
      this.powerUps
    );

    for (const powerUp of powerUpCollisions) {
      this.soundManager.play('powerup');
      this.applyPowerUp(powerUp);
      powerUp.active = false;
      
      // Power-up collection effect
      const config = powerUp.getConfig();
      this.particleSystem.createPowerUpEffect(powerUp.x, powerUp.y, config.color);
    }

    // Enemy bullets vs player (TODO: when bosses shoot)
    const bulletHits = this.collisionSystem.checkEnemyBulletPlayer(
      this.projectileSystem.getProjectiles(),
      this.player
    );

    if (bulletHits.length > 0) {
      this.soundManager.play('playerHit');
      this.player.takeDamage();
      this.scoreSystem.resetCombo();
    }

    // Clean up inactive power-ups
    this.powerUps = this.powerUps.filter(p => p.active);
  }

  private dropPowerUp(x: number, y: number): void {
    // 15% drop chance matching original
    if (Math.random() < 0.15) {
      this.powerUps.push(new PowerUp(x, y));
    }
  }

  private applyPowerUp(powerUp: PowerUp): void {
    const config = powerUp.getConfig();
    this.lastPowerUpName = config.name;
    this.powerUpNotificationTimer = 2;

    // Show Kid Kelly message for power-up
    this.kidKellyMessages.showPowerUpActivated(config.type);

    // Apply power-up to player
    this.player.applyPowerUp(config.type, config.duration);

    // Handle weapon switching for weapon power-ups
    switch (config.type) {
      case 'wave_signal':
        this.weaponSystem.switchWeapon(2); // WeaponType.WAVE_SIGNAL
        break;
      case 'dual_cannon':
        this.weaponSystem.switchWeapon(3); // WeaponType.DUAL_CANNON
        break;
      case 'machine_gun':
        this.weaponSystem.switchWeapon(4); // WeaponType.MACHINE_GUN
        break;
      case 'shotgun_blast':
        this.weaponSystem.switchWeapon(5); // WeaponType.SHOTGUN_BLAST
        break;
      case 'fm_super_blast':
        this.weaponSystem.switchWeapon(6); // WeaponType.FM_SUPER_BLAST
        break;
    }

    // Handle special effects
    switch (config.type) {
      case 'starburst':
        // Clear all enemies on screen
        for (const enemy of this.waveManager.getEnemies()) {
          this.particleSystem.createExplosion(enemy.x, enemy.y, '#ffffff', 30);
          enemy.takeDamage(9999);
          this.scoreSystem.addScore(enemy.getScore(), this.player.getScoreMultiplier());
        }
        this.projectileSystem.clearEnemyBullets();
        break;
      case 'double_chain':
        this.chainSystem.enableDoubleChain(config.duration);
        break;
      case 'repair':
        // Repair all chain segments
        this.unityChain.repairAllSegments();
        this.soundManager.play('powerUp');
        // Healing sparkle effect at bottom of screen
        for (let i = 0; i < 20; i++) {
          this.particleSystem.createPowerUpEffect(
            Math.random() * this.game.getCanvas().width,
            this.game.getCanvas().height - 80,
            '#00ff00'
          );
        }
        break;
      case 'shield':
        // Activate chain shield
        this.unityChain.activateShield(config.duration);
        this.soundManager.play('powerUp');
        // Shield activation burst
        for (let i = 0; i < 15; i++) {
          this.particleSystem.createPowerUpEffect(
            Math.random() * this.game.getCanvas().width,
            this.game.getCanvas().height - 80,
            '#4444ff'
          );
        }
        break;
    }
  }

  private onWaveComplete(): void {
    // Play wave complete sound
    this.soundManager.play('waveComplete');
    
    // Show Kid Kelly message
    this.kidKellyMessages.showWaveComplete(this.currentWave);
    
    // Add citizens to chain
    this.chainSystem.addWaveComplete();
    
    // Wave complete bonus
    this.scoreSystem.addBonus(100 * this.currentWave);

    // Wave complete celebration effect
    const canvas = this.game.getCanvas();
    for (let i = 0; i < 30; i++) {
      this.particleSystem.createPowerUpEffect(
        Math.random() * canvas.width,
        Math.random() * canvas.height * 0.5,
        '#ffff00'
      );
    }

    // Set up next wave transition
    this.waveTransitionTimer = 3;
  }

  private getRegionOverlayColor(): string {
    // Different color overlays for each region to provide visual variety
    // while maintaining gameplay visibility
    const regionColors = [
      'rgba(0, 50, 100, 0.65)',    // Region 1 - Ocean blue (East Coast)
      'rgba(100, 70, 0, 0.65)',    // Region 2 - Golden amber (Midwest)
      'rgba(120, 40, 0, 0.65)',    // Region 3 - Desert orange (Southwest)
      'rgba(100, 0, 80, 0.65)',    // Region 4 - Sunset magenta (West Coast)
      'rgba(0, 80, 120, 0.65)',    // Region 5 - Capitol cyan (Washington D.C.)
      'rgba(0, 100, 50, 0.65)',    // Region 6 - Forest green
      'rgba(80, 0, 120, 0.65)',    // Region 7 - Royal purple
      'rgba(120, 0, 0, 0.65)',     // Region 8 - Deep red
    ];
    
    // Wave-based intensity variation (slightly lighter on later waves)
    const waveIntensity = 0.65 + (this.currentWave * 0.02);
    const colorIndex = (this.currentRegion - 1) % regionColors.length;
    
    return regionColors[colorIndex].replace('0.65', waveIntensity.toFixed(2));
  }

  private advanceWave(): void {
    this.currentWave++;

    // Check if region is complete (5 waves per region as per Level Architecture)
    if (this.currentWave > 5) {
      this.currentWave = 1;
      this.currentRegion++;
      
      // Load new background for the new region
      this.updateBackgroundForRegion(this.currentRegion);

      // Check if game is complete (10 regions total = 50 levels)
      if (this.currentRegion > 10) {
        // Victory!
        const gameOverState = this.game.getStateManager().getCurrentState() as any;
        if (gameOverState && gameOverState.setScore) {
          gameOverState.setScore(this.scoreSystem.getScore());
          gameOverState.setVictory(true);
        }
        this.game.getStateManager().changeState('gameover');
        return;
      }
    }

    // Start next wave
    this.startNextWave();
  }

  public render(ctx: CanvasRenderingContext2D): void {
    const renderer = this.game.getRenderer();
    
    // Draw background image with dark overlay for gameplay visibility
    if (this.bgImageLoaded && this.backgroundImage) {
      // Calculate scaling - 10% smaller than canvas
      const imgAspect = this.backgroundImage.width / this.backgroundImage.height;
      const canvasAspect = renderer.width / renderer.height;
      
      let drawWidth = renderer.width * 0.9;
      let drawHeight = renderer.height * 0.9;
      let offsetX = renderer.width * 0.05;
      let offsetY = renderer.height * 0.05;
      
      if (imgAspect > canvasAspect) {
        drawWidth = drawHeight * imgAspect;
        offsetX = (renderer.width - drawWidth) / 2;
      } else {
        drawHeight = drawWidth / imgAspect;
        offsetY = (renderer.height - drawHeight) / 2;
      }
      
      ctx.drawImage(this.backgroundImage, offsetX, offsetY, drawWidth, drawHeight);
      
      // Color overlay based on current region/wave for visual variety
      const overlayColor = this.getRegionOverlayColor();
      ctx.fillStyle = overlayColor;
      ctx.fillRect(0, 0, renderer.width, renderer.height);
    } else {
      // Fallback to solid color
      renderer.clear('#0a0a0f');
    }
    
    // Draw Backtrax logo in center/middle of gameplay canvas
    if (this.backtraxLogoLoaded && this.backtraxLogo) {
      const logoMaxWidth = renderer.width * 0.5;
      const logoMaxHeight = renderer.height * 0.3;
      const logoAspect = this.backtraxLogo.width / this.backtraxLogo.height;
      
      let logoDrawWidth = logoMaxWidth;
      let logoDrawHeight = logoDrawWidth / logoAspect;
      
      if (logoDrawHeight > logoMaxHeight) {
        logoDrawHeight = logoMaxHeight;
        logoDrawWidth = logoDrawHeight * logoAspect;
      }
      
      // Position in the center/middle of the canvas, moved up 100px
      const logoX = (renderer.width - logoDrawWidth) / 2;
      const logoY = (renderer.height - logoDrawHeight) / 2 - 100;
      
      // Draw with slight transparency so it doesn't obstruct gameplay too much
      ctx.globalAlpha = 0.35;
      ctx.drawImage(this.backtraxLogo, logoX, logoY, logoDrawWidth, logoDrawHeight);
      ctx.globalAlpha = 1.0;
    }

    // Render Unity Chain at bottom
    this.unityChain.render(ctx);

    // Render game entities
    this.waveManager.render(ctx);
    this.projectileSystem.render(ctx);
    
    for (const powerUp of this.powerUps) {
      if (powerUp.active) {
        powerUp.render(ctx);
      }
    }
    
    this.player.render(ctx);
    
    // Render particle effects (on top of entities)
    this.particleSystem.render(ctx);

    // Render HUD
    this.hud.render(
      ctx,
      this.player,
      this.scoreSystem,
      this.chainSystem,
      this.currentRegion,
      this.currentWave
    );

    // Render overlays
    if (this.bossWarningTimer > 0) {
      this.hud.renderBossWarning(ctx, this.bossName);
    }

    if (this.waveTransitionTimer > 0) {
      this.hud.renderWaveComplete(ctx);
    }

    if (this.powerUpNotificationTimer > 0) {
      this.hud.renderPowerUpNotification(ctx, this.lastPowerUpName, this.powerUpNotificationTimer);
    }
  }

  private playWeaponSound(weaponType: number): void {
    // Map weapon types to sound effects
    switch (weaponType) {
      case 1: // Love Wave (FM Wave)
        this.soundManager.play('fmWave');
        break;
      case 2: // Light Laser
        this.soundManager.play('laser');
        break;
      case 3: // Rainbow Ray (Dual Shot)
        this.soundManager.play('dualShot');
        break;
      case 4: // Hope Burst (Machine Gun)
        this.soundManager.play('machineGun');
        break;
      case 5: // Unity Spread (Shotgun)
        this.soundManager.play('shotgun');
        break;
      case 6: // Harmony Blast (Super Blast)
        this.soundManager.play('superBlast');
        break;
    }
  }
  
  // Load background image based on region (levels 1-50 map to 10 regions)
  private updateBackgroundForRegion(region: number): void {
    const backgroundMap: Record<number, string> = {
      1: 'assets/background/Foundations (Levels 1–5).png',
      2: 'assets/background/Industrial Pressure (Levels 6–10).png',
      3: 'assets/background/Heartland (Levels 11–15).png',
      4: 'assets/background/SouthernHeat (Levels 16–20).png',
      5: 'assets/background/Coastal Divide (Levels 21–25).png',
      6: 'assets/background/MountainResolve (Levels 26-30).png',
      7: 'assets/background/Frontier Chaos (Levels 31–35).png',
      8: 'assets/background/Neon Future (Levels 36–40).png',
      9: 'assets/background/Fracture Zone (Levels 41–45).png',
      10: 'assets/background/Unity Restored (Levels 46–50).png'
    };
    
    const bgPath = backgroundMap[region] || backgroundMap[1];
    
    this.backgroundImage = new Image();
    this.backgroundImage.src = bgPath;
    this.backgroundImage.onload = () => {
      this.bgImageLoaded = true;
      console.log(`Loaded background for region ${region}`);
    };
  }
}
